import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Menu, User, LogOut, LayoutDashboard, Search } from "lucide-react";

export function Header() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useI18n();
  const nav = useNavigate();

  const dashPath = user?.role === "admin" ? "/admin" : user?.role === "provider" ? "/provider" : "/dashboard";

  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center">
          <img src="/brand/refine-touch-logo-dark.jpg" alt="Refine Touch" className="h-14 sm:h-16 w-auto object-contain" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-white/80">
          <Link to="/services" data-testid="nav-browse" className="hover:text-white transition-colors">{t("nav_browse")}</Link>
          <Link to="/categories" data-testid="nav-categories" className="hover:text-white transition-colors">{t("nav_categories")}</Link>
          <Link to="/providers" data-testid="nav-providers" className="hover:text-white transition-colors">{t("nav_providers")}</Link>
          <Link to="/how-it-works" data-testid="nav-how" className="hover:text-white transition-colors">{t("nav_how")}</Link>
          <Link to="/register?role=provider" data-testid="nav-become-provider" className="text-white border border-white/40 rounded-full px-4 py-1.5 hover:bg-white hover:text-black transition-colors">{t("become_provider")}</Link>
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1 text-xs text-white/60 border border-white/20 rounded-full px-1 py-1">
            <button data-testid="lang-fr" onClick={() => setLang("fr")} className={`px-2 py-0.5 rounded-full ${lang === "fr" ? "bg-white text-black" : ""}`}>FR</button>
            <button data-testid="lang-en" onClick={() => setLang("en")} className={`px-2 py-0.5 rounded-full ${lang === "en" ? "bg-white text-black" : ""}`}>EN</button>
          </div>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu" variant="ghost" className="rounded-full h-9 px-3 gap-2 text-white hover:bg-white/10 hover:text-white">
                  {user.avatar ? <img src={user.avatar} className="w-6 h-6 rounded-full object-cover" alt="" /> : <User size={18} />}
                  <span className="hidden sm:inline text-sm">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-white">
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => nav(dashPath)}>
                  <LayoutDashboard size={16} className="mr-2" />{t("nav_dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/profile")}>
                  <User size={16} className="mr-2" />{t("nav_profile")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={() => { logout(); nav("/"); }}>
                  <LogOut size={16} className="mr-2" />{t("nav_logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login"><Button data-testid="nav-login" variant="ghost" className="rounded-full text-white hover:bg-white/10 hover:text-white">{t("nav_login")}</Button></Link>
              <Link to="/register"><Button data-testid="nav-signup" className="rounded-full bg-white hover:bg-stone-200 text-black btn-pill">{t("nav_signup")}</Button></Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  return (
    <footer className="bg-black text-white mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-4 gap-8">
        <div>
          <img src="/brand/refine-touch-logo-dark.jpg" alt="Refine Touch" className="h-16 w-auto object-contain" />
          <p className="text-sm text-white/70 mt-4 max-w-xs">{t("footer_tag")}</p>
        </div>
        <div>
          <div className="eyebrow text-white/50 mb-3">{t("footer_discover")}</div>
          <ul className="space-y-2 text-sm text-white">
            <li><Link to="/services" className="hover:underline">{t("nav_browse")}</Link></li>
            <li><Link to="/categories" className="hover:underline">{t("nav_categories")}</Link></li>
            <li><Link to="/providers" className="hover:underline">{t("nav_providers")}</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-white/50 mb-3">{t("footer_help")}</div>
          <ul className="space-y-2 text-sm text-white">
            <li><Link to="/how-it-works" className="hover:underline">{t("nav_how")}</Link></li>
            <li><Link to="/register?role=provider" className="hover:underline">{t("become_provider")}</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-white/50 mb-3">{t("footer_legal")}</div>
          <ul className="space-y-2 text-sm text-white">
            <li><Link to="/terms" data-testid="footer-terms" className="hover:underline">{t("legal_terms")}</Link></li>
            <li><Link to="/privacy" data-testid="footer-privacy" className="hover:underline">{t("legal_privacy")}</Link></li>
            <li><Link to="/cookies" data-testid="footer-cookies" className="hover:underline">{t("legal_cookies")}</Link></li>
            <li><Link to="/refund-policy" data-testid="footer-refund" className="hover:underline">{t("legal_refund")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center text-xs text-white/60">© 2026 Refine Touch USA</div>
    </footer>
  );
}
